import { SearchBox } from '@/components/Search'
import Data from '@/data'
import Trans from '@/data/trans'
import { WithPassiveSkillTooltip } from '@/tooltip'
import { underlinerFunc } from '@/ui/select'
import { searchRegex } from '@/utils'
import { notNull } from '@/utils/validation'
import React, { useContext } from 'react'
import { FocusAction, TreeDataContext } from './common'
import { PassiveSidebar } from './sidebar'

function PassiveLineKeystone({
  id,
  setFocus,
  search
}: {
  id: number
  setFocus: (id: number | undefined, center?: boolean) => void
  search?: RegExp
}) {
  const data = useContext(TreeDataContext)
  const skill = data.nodeSkill(id)!
  if (search && !skill.name.match(search)) return null
  return (
    <WithPassiveSkillTooltip skill={skill} atlas={data.atlas}>
      <div
        className="poe-line poe-keystone"
        onMouseEnter={() => setFocus(id)}
        onMouseLeave={() => setFocus(undefined)}
        onClick={() => setFocus(id, true)}>
        {underlinerFunc(skill.name, search)}
      </div>
    </WithPassiveSkillTooltip>
  )
}

function PassiveLineText({
  id,
  text,
  setFocus,
  search
}: {
  id: number
  text: string
  setFocus: (id: number | undefined, center?: boolean) => void
  search?: RegExp
}) {
  return (
    <li
      className="poe-line"
      onMouseEnter={() => setFocus(id)}
      onMouseLeave={() => setFocus(undefined)}
      onClick={() => setFocus(id, true)}>
      {underlinerFunc(text, search)}
    </li>
  )
}

const statCategories = [
  { id: 'Attributes', name: 'Attributes' },
  { id: 'Damage', name: 'Damage' },
  { id: 'Ailments', name: 'Ailments' },
  { id: 'Skill Speed', name: 'Skill Speed' },
  { id: 'Critical Strikes', name: 'Critical Strikes' },
  { id: 'Accuracy', name: 'Accuracy' },
  { id: 'Other Offensive Stats', name: 'Other Offensive Stats' },
  { id: 'Life', name: 'Life' },
  { id: 'Mana', name: 'Mana' },
  { id: 'Defences', name: 'Defences' },
  { id: 'Resistances', name: 'Resistances' },
  { id: 'Other Survivability Stats', name: 'Other Survivability Stats' },
  { id: 'Minions', name: 'Minions' },
  { id: 'Totems', name: 'Totems' },
  { id: 'Traps', name: 'Traps' },
  { id: 'Mines', name: 'Mines' },
  { id: 'Charges', name: 'Charges' },
  { id: 'Flasks', name: 'Flasks' },
  { id: 'Misc', name: 'Misc' },
  { id: 'Ascendancy', name: 'Ascendancy' }
]

export function PassiveTotals({
  setFocus
}: {
  setFocus: (action: FocusAction | undefined) => void
}) {
  const [search, setSearch] = React.useState('')
  const data = useContext(TreeDataContext)
  const { keystones, categories } = React.useMemo(() => {
    const { stats, auraStats, keystones } = data.totalStats()
    const categories: Record<
      string,
      {
        stats: Record<string, number>
        auraStats: Record<string, number>
      }
    > = {}
    function category(id: string) {
      const cat = Data.stats[id]?.category ?? 'Misc'
      if (!categories[cat]) categories[cat] = { stats: {}, auraStats: {} }
      return categories[cat]
    }
    for (const [id, value] of Object.entries(stats)) {
      if (value) category(id).stats[id] = value
    }
    for (const [id, value] of Object.entries(auraStats)) {
      if (value) category(id).auraStats[id] = value
    }
    return {
      keystones,
      categories: statCategories
        .map(({ id, name }) => {
          if (!categories[id]) return
          return {
            name,
            lines: Trans[data.atlas ? 'atlas' : 'passive_skill'].formatStats(
              categories[id].stats,
              true
            ),
            auraLines: Trans.passive_skill_aura.formatStats(
              categories[id].auraStats,
              true
            )
          }
        })
        .filter(notNull)
    }
  }, [data])

  const onFocusKeystone = React.useCallback(
    (id: number | undefined, center?: boolean) =>
      setFocus(id ? { nodes: [id], center } : undefined),
    [setFocus]
  )
  const onFocusStat = React.useCallback(
    (id: number | undefined, center?: boolean) => {
      if (!id) {
        setFocus(undefined)
        return
      }
      const trans = Trans[data.atlas ? 'atlas' : 'passive_skill'][id]
      if (!trans) return
      setFocus({
        nodes: [...data.activeNodes()].filter((id) => {
          const skill = data.nodeSkill(id)
          if (!skill || skill.is_keystone || skill.disabled) return false
          if (trans.ids.some((id) => skill.stats[id])) return true
          if (skill.mastery) {
            const effect = skill.mastery.effects[data.node(id)!.masteryIndex!]
            if (effect && trans.ids.some((id) => effect[id])) return true
          }
          return false
        }),
        center
      })
    },
    [data, setFocus]
  )
  const onFocusAura = React.useCallback(
    (id: number | undefined, center?: boolean) => {
      if (!id) {
        setFocus(undefined)
        return
      }
      const trans = Trans.passive_skill_aura[id]
      if (!trans) return
      setFocus({
        nodes: [...data.activeNodes()].filter((id) => {
          const skill = data.nodeSkill(id)
          if (!skill || skill.is_keystone || skill.disabled) return false
          if (skill.aura && trans.ids.some((id) => skill.aura!.stats[id]))
            return true
          return false
        }),
        center
      })
    },
    [data, setFocus]
  )

  const searchRe = React.useMemo(() => searchRegex(search, true), [search])
  const searchResults = React.useMemo(() => {
    if (!searchRe) return undefined
    const lines: string[] = []
    const auraLines: string[] = []
    for (const cat of categories) {
      cat.lines.forEach((line, id) => {
        if (line.match(searchRe)) lines[id] = line
      })
      cat.auraLines.forEach((line, id) => {
        if (line.match(searchRe)) auraLines[id] = line
      })
    }
    return { lines, auraLines }
  }, [searchRe, categories])

  if (!keystones.length && !categories.length) return null

  return (
    <PassiveSidebar className="poe-totals" title="Stats">
      <SearchBox search={search} setSearch={setSearch} placeholder="Search" />
      {keystones.map((id) => (
        <PassiveLineKeystone
          key={id}
          id={id}
          setFocus={onFocusKeystone}
          search={searchRe}
        />
      ))}
      {searchResults ? (
        <ul>
          {searchResults.lines.map((text, id) => (
            <PassiveLineText
              key={id}
              id={id}
              text={text}
              setFocus={onFocusStat}
              search={searchRe}
            />
          ))}
          {searchResults.auraLines.map((text, id) => (
            <PassiveLineText
              key={id}
              id={id}
              text={text}
              setFocus={onFocusAura}
              search={searchRe}
            />
          ))}
        </ul>
      ) : (
        categories.map(({ name, lines, auraLines }) => (
          <React.Fragment key={name}>
            <h3>{name}</h3>
            <ul>
              {lines.map((text, id) => (
                <PassiveLineText
                  key={id}
                  id={id}
                  text={text}
                  setFocus={onFocusStat}
                />
              ))}
              {auraLines.map((text, id) => (
                <PassiveLineText
                  key={id}
                  id={id}
                  text={text}
                  setFocus={onFocusAura}
                />
              ))}
            </ul>
          </React.Fragment>
        ))
      )}
    </PassiveSidebar>
  )
}
